import React from "react";
import styled from "styled-components";
import { useSnapshot } from "valtio";
import {
    RefreshIcon,
    StyledIconButton,
    TrashIcon,
} from "@aureskonnect/react-ui";
import { t } from "i18next";
import ReactSwitch from "react-switch";
import { IoIosMenu } from "react-icons/io";
import swal from "@sweetalert/with-react";

import { Uploader } from "@components/Uploader";

import {
    generalConfigStore,
    setDeletedStandbyStateScreenItemId,
    setIsDeleteStandbyStateScreenItemModalOpened,
    setStandbyScreenFile,
    setStandbyStateScreenItems,
} from "@store";

import { Table } from "./Table";
import { DeleteModal } from "./DeleteModal";
import { store } from "../../../../../../../store/project";
import NProgressWrapper from "@components/Common/NProgressWrapper";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { useFetchGlobal } from "../../../../../../../api/hooks";

const Styles = styled.div`
    padding: 0.5rem;
    height: 420px;
    table {
        table-layout: fixed;
        border-spacing: 0;
        border-collapse: collapse;
        transition: background-color 0.5s ease;
        thead {
            border-bottom: 2px solid black;
            tr {
                :first-child {
                    color: black;
                    th {
                        :first-child {
                            text-align: left;
                        }
                        text-align: left;
                    }
                }
            }
        }
        th,
        td {
            margin: 10px;
            padding: 0.5rem;
            height: 30px;
            width: 30%;
            border-right: 0px;
            border-left: 0px;
            :first-child {
                width: 15%;
                input {
                    width: auto;
                    text-align: left;
                }
            }
            :last-child {
                width: 10%;
                text-align: right;
            }
            input {
                width: 35%;
                text-align: center;
                background-color: inherit;
                font-size: 1rem;
                padding: 5px;
                margin: 0;
                border-radius: 5px;
            }
        }
    }
`;

// eslint-disable-next-line
const UpDownArrow = ({ dragHandleProps, className }: any) => (
    <div
        {...dragHandleProps}
        className={className}
        aria-label="move"
        role="img"
    >
        <IoIosMenu
            style={{
                fill: "#848484",
                width: "50px",
                height: "50px",
            }}
        />
    </div>
);

export function StandbyStateScreenTable(): JSX.Element {
    const {
        standbyStateScreenItems,
        defaultStandbyStateScreenItems,
        isDeleteStandbyStateScreenItemModalOpened,
    } = useSnapshot(generalConfigStore);
    const { isLoading } = useSnapshot(store);
    const uploadInputRef = React.useRef() as React.MutableRefObject<
        HTMLInputElement
    >;
    const { fetchGlobal } = useFetchGlobal();

    const activeScreens: StandbyScreenItemType[] = standbyStateScreenItems.filter(
        (item: StandbyScreenItemType) => item.active
    );

    const validUploadedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "video/mp4",
        "video/webm",
    ];

    // eslint-disable-next-line
    function OrderCell({ row, dragHandleProps, className }: any) {
        return (
            <div
                className="d-flex align-items-center position-relative"
                style={{ gap: "20px" }}
            >
                <UpDownArrow
                    dragHandleProps={dragHandleProps}
                    className={className}
                />

                <span style={{ font: "normal normal 600 20px Segoe UI" }}>
                    {row.index + 1}
                </span>
            </div>
        );
    }

    // eslint-disable-next-line
    function EditableNumberCell({ row, cell }: any) {
        const [value, setValue] = React.useState(cell.value);
        const [isFieldEmpty, setIsFieldEmpty] = React.useState(false);

        const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let value = e.target.value;

            if (value.startsWith("0") && value.length > 1) {
                value = value.replace(/^0+/, "");
            }

            if (value === "0" || value === "") {
                setIsFieldEmpty(true);
                value = "1";
            } else {
                if (isFieldEmpty) {
                    setIsFieldEmpty(false);

                    if (value.startsWith("1")) {
                        value = value.slice(1);
                    }
                }
            }

            if (value === "" || /^[1-9][0-9]*$/.test(value)) {
                setValue(Math.abs(Number(value)));
            }
        };

        const onBlur = () => {
            const localData = [...standbyStateScreenItems];
            localData[row.id] = {
                ...localData[row.id],
                timeout: Number(value),
            };

            setStandbyStateScreenItems(localData);
            setStandbyScreenFile(localData);
        };

        return (
            <div data-mdb-input-init className="form-outline">
                <input
                    disabled={row.original.type.includes("video")}
                    type="number"
                    id="typeNumber"
                    className="form-control epb_inp_timeAS"
                    title="input"
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    min={1}
                    defaultValue={row.original.timeout}
                />
            </div>
        );
    }

    // eslint-disable-next-line
    function ActionsCell({ row }: any) {
        return (
            <div className="d-flex align-items-center justify-content-end position-relative">
                <ReactSwitch
                    disabled={
                        activeScreens.length === 1 && row.original.active
                            ? true
                            : false
                    }
                    uncheckedIcon={false}
                    checkedIcon={false}
                    handleDiameter={26}
                    offColor="#f7b4b8"
                    offHandleColor="#E30613"
                    checked={row.original.active}
                    onChange={() => {
                        const localData = [...standbyStateScreenItems];
                        localData[row.id] = {
                            ...localData[row.id],
                            active: !row.original.active,
                        };

                        setStandbyStateScreenItems(localData);
                        setStandbyScreenFile(localData);
                    }}
                    onColor="#c2eddd"
                    onHandleColor="#34C38F"
                    width={55}
                    height={20}
                />
                <StyledIconButton
                    icon="TrashIcon"
                    disabled={
                        activeScreens.length === 1 && row.original.active
                            ? true
                            : false
                    }
                    onClick={() => {
                        setDeletedStandbyStateScreenItemId(row.original.id);
                        setIsDeleteStandbyStateScreenItemModalOpened(
                            !isDeleteStandbyStateScreenItemModalOpened
                        );
                    }}
                    aria-label="delete"
                >
                    <TrashIcon
                        style={{
                            fill: "red",
                        }}
                        height={20}
                        width={20}
                    />
                </StyledIconButton>
            </div>
        );
    }

    // eslint-disable-next-line
    function UploaderCell({ row }: any) {
        return (
            <Uploader
                uploadedFileTitle={
                    row.original.content.split("/")[
                        row.original.content.split("/").length - 1
                    ]
                }
                id={row.original.id}
                screenItemId={row.original.id}
                screenItems={standbyStateScreenItems}
                setScreenItems={setStandbyStateScreenItems}
                updateFrom="StandbyStateScreen"
                localizator="epb_icn_editFileSS"
            />
        );
    }

    const columns = React.useMemo(
        () => [
            {
                Header: t("Order"),
                accessor: t("Order"),
                Cell: OrderCell,
            },
            {
                Header: t("Image or video"),
                accessor: "content",
                Cell: UploaderCell,
            },
            {
                Header: t("Display time (sec)"),
                accessor: "timeout",
                Cell: EditableNumberCell,
            },
            {
                Header: t("Actions"),
                accessor: t("Actions"),
                Cell: ActionsCell,
            },
        ],
        // eslint-disable-next-line
        [
            activeScreens,
            setStandbyStateScreenItems,
            standbyStateScreenItems,
            setIsDeleteStandbyStateScreenItemModalOpened,
            isDeleteStandbyStateScreenItemModalOpened,
        ]
    );

    function handleUploadImageOnClickEvent(): void {
        uploadInputRef?.current?.click();
    }

    async function handleUploadedDataOnChangeEvent(
        event: React.ChangeEvent<HTMLInputElement>
    ) {
        // eslint-disable-next-line
        const fileTarget = (event.target as HTMLInputElement)?.files;

        if (fileTarget !== null) {
            handleUploadingNewFileRequest((fileTarget as FileList)[0]);
        }
    }

    async function handleUploadingNewFileRequest(file: File | undefined) {
        const apiUrl = `${process.env.REACT_APP_API_V2_URL}/kiosk/upload`;

        if (file !== undefined) {
            if (!validUploadedTypes.includes(file.type)) {
                return swal({
                    icon: "error",
                    content: <p>{t("File type is not valid")}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            }
            const dataSend = new FormData();
            dataSend.append("file", file as File);
            try {
                const data: any = await fetchGlobal(apiUrl, {
                    body: dataSend,
                    method: "PUT",
                });
                if (data.error) {
                    throw Error(data.message);
                }
                const localData: StandbyScreenItemType[] = [
                    ...standbyStateScreenItems,
                ];
                const searchedFileIndex: number = localData.indexOf(
                    // eslint-disable-next-line
                    localData.find((element) => element.id === data.data.id)!
                );

                if (searchedFileIndex !== -1) {
                    localData[searchedFileIndex] = {
                        ...localData[searchedFileIndex],
                        content: data.data.content,
                        timeout: data.data.type.includes("video")
                            ? data.data.timeout
                            : 3,
                        type: data.data.type,
                    };
                } else {
                    localData.push({
                        id: data.data.id,
                        active: data.data.active,
                        content: data.data.content,
                        timeout: data.data.type.includes("video")
                            ? data.data.timeout
                            : 3,
                        type: data.data.type,
                    });
                }
                setStandbyStateScreenItems(localData);
                setStandbyScreenFile(localData);
                return swal({
                    icon: "success",
                    content: <p>{t(data.message)}!</p>,
                    buttons: false,
                    timer: 2000,
                });
            } catch (error: any) {
                return swal({
                    icon: "error",
                    content: <p>{t(error.message)}</p>,
                    buttons: false,
                    timer: 2000,
                });
            }
        }
    }

    async function handleResetOnClickEvent(): Promise<void> {
        const defaultItems = [
            {
                id: "767a92f4-bf99-40ca-9932-c1e2d2464478",
                type: "video",
                active: true,
                content: "standbyStateScreen.webm",
                timeout: 19,
            },
        ];
        setStandbyStateScreenItems(defaultItems);
        uploadInputRef.current.value = "";
    }

    return (
        <div
            className="d-flex flex-column cursor__clz rounded border"
            style={{ gap: "10px", borderColor: "#CECECE" }}
        >
            <div
                className="text-left px-4 d-flex align-items-center cursor__clz"
                style={{
                    backgroundColor: "#EDEDED",
                    height: "40px",
                    fontSize: "20px",
                }}
            >
                {t("General")}
            </div>

            <div className="px-2 d-flex justify-content-between align-items-center">
                <span
                    style={{
                        font: "normal normal normal 15px/20px Nunito Sans",
                    }}
                >
                    {t("Reading list")}
                </span>

                <div
                    className="d-flex align-items-center"
                    style={{
                        gap: "10px",
                    }}
                >
                    <StyledIconButton
                        title={t("Reset to default settings")}
                        outline={true}
                        variant="danger"
                        className="m-0 p-0"
                        icon="RefreshIcon"
                        onClick={() => {
                            setStandbyStateScreenItems(
                                defaultStandbyStateScreenItems
                            );
                            setStandbyScreenFile(
                                defaultStandbyStateScreenItems
                            );
                            handleResetOnClickEvent();
                        }}
                    >
                        <RefreshIcon height={20} width={20} />
                    </StyledIconButton>

                    <div className="d-flex epb_btn_addFileAS">
                        <input
                            accept="video/*,image/*"
                            multiple={false}
                            type="file"
                            id="fileUpload"
                            hidden={true}
                            ref={uploadInputRef}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                handleUploadedDataOnChangeEvent(event);
                            }}
                            onClick={(event: any) => {
                                event.currentTarget.value = "";
                            }}
                        />
                        <CustomMainColorButton
                            rounded={true}
                            variant="primary"
                            onClick={() => {
                                handleUploadImageOnClickEvent();
                            }}
                        >
                            {t("Add")}
                        </CustomMainColorButton>
                    </div>
                </div>
            </div>

            <Styles>
                <Table columns={columns} />
            </Styles>
            <DeleteModal />
            {isLoading && <NProgressWrapper isLoading={isLoading} />}
        </div>
    );
}
